/**
 * Dark8-s Phase 18 — Asset Generation Sandbox Frontend Screens
 *
 * Screens:
 *   F. Asset Package     — prepare asset package (approved + draft only)
 *   G. Asset Review      — operator review controls (note mandatory)
 *   H. Production Export — final export package (all gates cleared)
 *
 * Governance invariants:
 *   - publish_allowed is always false. No publish buttons exist.
 *   - No upload buttons. No scheduler controls. No platform credentials.
 *   - Human operator is sole publishing authority.
 *   - Copy/download of the export package is allowed.
 */

import { useState } from "react";
import {
    AlertTriangle,
    CheckCircle,
    Copy,
    Download,
    FileText,
    Package,
    RefreshCw,
    ShieldOff,
    XCircle,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import {
    prepareAssetPackage,
    reviewAssetPackage,
    productionExport,
} from "../lib/api";

// ─── Shared sub-components ────────────────────────────────────────────────────

const GovernanceBanner = () => (
    <div
        data-testid="governance-banner"
        className="flex items-center gap-3 bg-[#ff2a2a]/10 border border-[#ff2a2a]/40 px-4 py-3 font-mono-d8 text-[11px] text-[#ff2a2a] uppercase tracking-widest"
    >
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>
            MANUAL PUBLISHING ONLY — publish_allowed=false — Human operator review required before any distribution.
        </span>
    </div>
);

const PublishBlockedMarker = () => (
    <span
        data-testid="publish-blocked-marker"
        className="inline-flex items-center gap-1 border border-[#ff2a2a]/40 bg-[#ff2a2a]/5 px-2 py-0.5 font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest"
    >
        <ShieldOff className="w-3 h-3" />
        publish_allowed=false
    </span>
);

const SectionHeader = ({ label, children }) => (
    <section className="border-b border-[#262626] px-6 md:px-12 py-8 noise-bg">
        <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest mb-2">{label}</div>
        {children}
    </section>
);

const BackButton = ({ onClick }) => (
    <button
        data-testid="back-button"
        onClick={onClick}
        className="inline-flex items-center gap-2 font-mono-d8 text-[11px] text-[#8a8a8a] hover:text-[#f5f5f0] uppercase tracking-widest transition-colors"
    >
        ← Back
    </button>
);

const ErrorBox = ({ msg }) =>
    msg ? (
        <div
            data-testid="error-box"
            className="border border-[#ff2a2a]/40 bg-[#ff2a2a]/5 p-3 font-mono-d8 text-xs text-[#ff2a2a]"
        >
            {typeof msg === "string" ? msg : JSON.stringify(msg)}
        </div>
    ) : null;

const FieldRow = ({ label, value }) => (
    <div className="border-b border-[#1a1a1a] py-3 grid grid-cols-[180px_1fr] gap-4">
        <span className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest pt-0.5">{label}</span>
        <span className="font-mono-d8 text-xs text-[#f5f5f0] whitespace-pre-wrap break-words">
            {Array.isArray(value) ? value.join(", ") : String(value ?? "—")}
        </span>
    </div>
);

// ─── Screen F: Asset Package ──────────────────────────────────────────────────

export const AssetPackageScreen = ({ concept, onBack, onNavigateToAssetReview }) => {
    const [pkg, setPkg] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const generate = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await prepareAssetPackage(concept.concept_id);
            setPkg(data);
            toast.success("Asset package prepared.");
        } catch (err) {
            const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || err?.message || "Asset package preparation failed.";
            setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        } finally {
            setLoading(false);
        }
    };

    const canGenerate = concept.review_status === "approved" && concept.script_draft;

    return (
        <div data-testid="asset-package-screen">
            <SectionHeader label="Asset Package">
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-2xl md:text-4xl leading-none">
                    Asset <span className="text-[#ff2a2a]">Package</span>
                </h1>
                <p className="mt-2 text-[#8a8a8a] text-sm">{concept.title}</p>
            </SectionHeader>
            <div className="px-6 md:px-12 py-8 space-y-8 max-w-4xl">
                <BackButton onClick={onBack} />
                <GovernanceBanner />

                {!canGenerate && (
                    <div
                        data-testid="asset-package-blocked"
                        className="border border-[#ff2a2a]/30 bg-[#ff2a2a]/5 p-4 font-mono-d8 text-xs text-[#ff2a2a] space-y-1"
                    >
                        <div className="font-bold uppercase tracking-widest">Asset Package Unavailable</div>
                        <div>
                            Requires <strong>approved</strong> status and a prepared script draft.
                            Current status: <strong>{concept.review_status || "unknown"}</strong>.
                        </div>
                    </div>
                )}

                {canGenerate && !pkg && (
                    <div className="space-y-4">
                        <div className="border border-[#262626] bg-[#0a0a0a] p-4 font-mono-d8 text-xs text-[#8a8a8a]">
                            Generate a complete asset package: shot list, scene breakdown, visual/video prompts,
                            voiceover, captions, hashtags, thumbnail guidance, audio/SFX cues, and render manifest.
                            publish_allowed=false. No publishing authority is granted.
                        </div>
                        <ErrorBox msg={error} />
                        <button
                            data-testid="generate-asset-package-btn"
                            onClick={generate}
                            disabled={loading}
                            className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] disabled:opacity-50 text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                        >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                            Generate Asset Package
                        </button>
                    </div>
                )}

                {pkg && (
                    <div data-testid="asset-package-result" className="space-y-6">
                        <div className="flex flex-wrap items-center gap-3">
                            <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                            <span className="font-mono-d8 text-xs text-[#22c55e]">Asset package ready — pending operator review</span>
                            <PublishBlockedMarker />
                        </div>

                        {/* Shot List */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Shot List</div>
                            {pkg.shot_list?.map((shot) => (
                                <div key={shot.shot_number} className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                                    <div className="font-mono-d8 text-[10px] text-[#ffb800] uppercase">Shot {shot.shot_number} — {shot.timestamp} ({shot.duration})</div>
                                    <FieldRow label="Framing" value={shot.framing} />
                                    <FieldRow label="Action" value={shot.action} />
                                    <FieldRow label="Timing Note" value={shot.comedic_timing_note} />
                                </div>
                            ))}
                        </div>

                        {/* Scene Breakdown */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Scene Breakdown</div>
                            <div className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                                <FieldRow label="Hook" value={pkg.scene_breakdown?.hook} />
                                <FieldRow label="Setup" value={pkg.scene_breakdown?.setup} />
                                <FieldRow label="Twist / Punchline" value={pkg.scene_breakdown?.twist_punchline} />
                            </div>
                        </div>

                        {/* Visual Prompts */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Visual Prompt Pack</div>
                            <div className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                                <FieldRow label="Style" value={pkg.visual_prompt_pack?.style_prompt} />
                                <FieldRow label="Environment" value={pkg.visual_prompt_pack?.environment_prompt} />
                                <FieldRow label="Character" value={pkg.visual_prompt_pack?.character_prompt} />
                                <FieldRow label="Negative" value={pkg.visual_prompt_pack?.negative_prompt} />
                                <FieldRow label="Format" value={pkg.visual_prompt_pack?.format_guidance} />
                            </div>
                        </div>

                        {/* Video Prompts */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Video Prompt Pack</div>
                            <div className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                                <FieldRow label="Motion" value={pkg.video_prompt_pack?.motion_prompt} />
                                <FieldRow label="Camera" value={pkg.video_prompt_pack?.camera_movement} />
                                <FieldRow label="Timing" value={pkg.video_prompt_pack?.timing_guidance} />
                                <FieldRow label="Transitions" value={pkg.video_prompt_pack?.transition_guidance} />
                            </div>
                        </div>

                        {/* Voiceover */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Voiceover Package</div>
                            <div className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                                <FieldRow label="Script" value={pkg.voiceover_package?.script} />
                                <FieldRow label="Delivery" value={pkg.voiceover_package?.delivery_style} />
                                <FieldRow label="Pacing" value={pkg.voiceover_package?.pacing} />
                                <FieldRow label="Pause Markers" value={pkg.voiceover_package?.pause_markers} />
                            </div>
                        </div>

                        {/* Captions */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Caption Package</div>
                            <div className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                                <FieldRow label="Subtitles" value={pkg.caption_package?.subtitles} />
                                <FieldRow label="Accessibility" value={pkg.caption_package?.accessibility_captions} />
                                <FieldRow label="Short Description" value={pkg.caption_package?.short_description} />
                            </div>
                        </div>

                        {/* Hashtags */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Hashtag Package</div>
                            <div className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                                <FieldRow label="Brand Tags" value={pkg.hashtag_package?.brand_tags} />
                                <FieldRow label="Topic Tags" value={pkg.hashtag_package?.topic_tags} />
                                <FieldRow label="Platform Tags" value={pkg.hashtag_package?.platform_neutral_tags} />
                            </div>
                        </div>

                        {/* Thumbnail */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Thumbnail Prompt</div>
                            <div className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                                <FieldRow label="Composition" value={pkg.thumbnail_prompt?.composition_guidance} />
                                <FieldRow label="Image Prompt" value={pkg.thumbnail_prompt?.image_prompt} />
                                <FieldRow label="Emphasis" value={pkg.thumbnail_prompt?.visual_emphasis_guidance} />
                            </div>
                        </div>

                        {/* Audio/SFX */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Audio / SFX Package</div>
                            <div className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                                <FieldRow label="Ambience" value={pkg.audio_sfx_package?.ambience} />
                                <FieldRow label="Sting" value={pkg.audio_sfx_package?.sting} />
                                <FieldRow label="Silence Beats" value={pkg.audio_sfx_package?.silence_beats} />
                                <FieldRow label="Punchline Cue" value={pkg.audio_sfx_package?.punchline_cue} />
                            </div>
                        </div>

                        {/* Render Manifest */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Render Manifest</div>
                            <div className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                                <FieldRow label="Aspect Ratio" value={pkg.render_manifest?.aspect_ratio} />
                                <FieldRow label="Duration" value={pkg.render_manifest?.duration} />
                                <FieldRow label="FPS Target" value={pkg.render_manifest?.fps_target} />
                                <FieldRow label="Export Notes" value={pkg.render_manifest?.export_notes} />
                                <FieldRow label="Safe Area" value={pkg.render_manifest?.safe_area_guidance} />
                            </div>
                        </div>

                        {/* Risk Notes */}
                        <div className="space-y-2">
                            <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest">Risk Notes</div>
                            <div className="border border-[#ff2a2a]/20 bg-[#ff2a2a]/5 p-3 space-y-1">
                                <FieldRow label="Classification" value={pkg.risk_notes?.classification_tier} />
                                <FieldRow label="Reason Codes" value={pkg.risk_notes?.reason_codes} />
                                <FieldRow label="Reminders" value={pkg.risk_notes?.review_reminders} />
                                <FieldRow label="Warnings" value={pkg.risk_notes?.operator_warnings} />
                            </div>
                        </div>

                        <button
                            data-testid="go-to-asset-review-btn"
                            onClick={() => onNavigateToAssetReview(concept, pkg)}
                            className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                        >
                            <FileText className="w-4 h-4" /> Review Asset Package
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Screen G: Asset Review ───────────────────────────────────────────────────

const ASSET_REVIEW_STATUSES = [
    { value: "asset_approved", label: "Approve" },
    { value: "asset_needs_revision", label: "Needs Revision" },
    { value: "asset_rejected", label: "Reject" },
];

export const AssetReviewScreen = ({ concept, onBack, onNavigateToProductionExport }) => {
    const [status, setStatus] = useState("asset_approved");
    const [note, setNote] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const submit = async () => {
        if (!note.trim()) {
            setError("Operator note is required.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await reviewAssetPackage(concept.concept_id, status, note.trim());
            setResult(data);
            toast.success(`Asset package ${status.replace("asset_", "").replace("_", " ")}.`);
        } catch (err) {
            const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || err?.message || "Review submission failed.";
            setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="asset-review-screen">
            <SectionHeader label="Asset Review">
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-2xl md:text-4xl leading-none">
                    Asset <span className="text-[#ff2a2a]">Review</span>
                </h1>
                <p className="mt-2 text-[#8a8a8a] text-sm">{concept.title}</p>
            </SectionHeader>
            <div className="px-6 md:px-12 py-8 space-y-8 max-w-2xl">
                <BackButton onClick={onBack} />
                <GovernanceBanner />

                {!result && (
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest">
                                Review Decision
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {ASSET_REVIEW_STATUSES.map((s) => (
                                    <button
                                        key={s.value}
                                        data-testid={`review-status-${s.value}`}
                                        onClick={() => setStatus(s.value)}
                                        className={`font-mono-d8 text-[11px] uppercase tracking-widest px-4 py-2 border transition-colors ${
                                            status === s.value
                                                ? "border-[#ff2a2a] text-[#ff2a2a] bg-[#ff2a2a]/10"
                                                : "border-[#262626] text-[#8a8a8a] hover:border-[#ff2a2a] hover:text-[#f5f5f0]"
                                        }`}
                                    >
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest">
                                Operator Note <span className="text-[#ff2a2a]">*</span>
                            </label>
                            <textarea
                                data-testid="asset-review-note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Required. Explain your decision."
                                rows={4}
                                className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3 focus:outline-none focus:border-[#ff2a2a] resize-none"
                            />
                        </div>

                        <ErrorBox msg={error} />

                        <button
                            data-testid="submit-asset-review-btn"
                            onClick={submit}
                            disabled={loading || !note.trim()}
                            className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] disabled:opacity-50 text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                        >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                            Submit Review
                        </button>
                    </div>
                )}

                {result && (
                    <div data-testid="asset-review-result" className="space-y-4">
                        <div className="flex items-center gap-3">
                            <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                            <span className="font-mono-d8 text-xs text-[#22c55e]">Review submitted</span>
                            <PublishBlockedMarker />
                        </div>
                        <div className="border border-[#262626] bg-[#0a0a0a] p-3 space-y-1">
                            <FieldRow label="Status" value={result.asset_package_status} />
                            <FieldRow label="Operator Note" value={result.operator_note} />
                            <FieldRow label="publish_allowed" value={String(result.publish_allowed)} />
                        </div>

                        {result.asset_package_status === "asset_approved" && (
                            <button
                                data-testid="go-to-production-export-btn"
                                onClick={() => onNavigateToProductionExport(concept)}
                                className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                            >
                                <Download className="w-4 h-4" /> Generate Production Export
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Screen H: Production Export ─────────────────────────────────────────────

export const ProductionExportScreen = ({ concept, onBack }) => {
    const [exportPkg, setExportPkg] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const generate = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await productionExport(concept.concept_id);
            setExportPkg(data);
            toast.success("Production export package ready.");
        } catch (err) {
            const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || err?.message || "Export failed.";
            setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        } finally {
            setLoading(false);
        }
    };

    const copyJson = () => {
        if (!exportPkg) return;
        navigator.clipboard?.writeText(JSON.stringify(exportPkg, null, 2)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div data-testid="production-export-screen">
            <SectionHeader label="Production Export">
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-2xl md:text-4xl leading-none">
                    Production <span className="text-[#ff2a2a]">Export</span>
                </h1>
                <p className="mt-2 text-[#8a8a8a] text-sm">{concept.title}</p>
            </SectionHeader>
            <div className="px-6 md:px-12 py-8 space-y-8 max-w-4xl">
                <BackButton onClick={onBack} />

                {/* Critical warning — always visible */}
                <div
                    data-testid="production-export-warning"
                    className="border-2 border-[#ff2a2a] bg-[#ff2a2a]/10 p-5 space-y-2"
                >
                    <div className="flex items-center gap-2 font-mono-d8 text-sm text-[#ff2a2a] font-bold uppercase tracking-widest">
                        <AlertTriangle className="w-5 h-5" /> Critical
                    </div>
                    <p className="font-mono-d8 text-xs text-[#ff2a2a]">
                        MANUAL PRODUCTION EXPORT ONLY. No autonomous or automated release authority is
                        granted by this system. publish_allowed=false. This package must be manually
                        used by a human operator. No upload buttons exist. No publish buttons exist.
                        No platform integrations exist.
                    </p>
                </div>

                {/* Verify no publish controls exist */}
                {/* (No publish button, no upload button, no scheduler control rendered here) */}

                {!exportPkg && (
                    <div className="space-y-4">
                        <div className="border border-[#262626] bg-[#0a0a0a] p-4 font-mono-d8 text-xs text-[#8a8a8a]">
                            Generate the final production export package. Requires approved concept,
                            prepared script draft, prepared asset package, and approved asset review.
                            This package does not grant publishing authority.
                        </div>
                        <ErrorBox msg={error} />
                        <button
                            data-testid="generate-production-export-btn"
                            onClick={generate}
                            disabled={loading}
                            className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] disabled:opacity-50 text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                        >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            Generate Production Export
                        </button>
                    </div>
                )}

                {exportPkg && (
                    <div data-testid="production-export-result" className="space-y-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                            <span className="font-mono-d8 text-xs text-[#22c55e]">Production export package ready</span>
                            <PublishBlockedMarker />
                        </div>

                        {exportPkg.production_export_header && (
                            <div className="border border-[#ff2a2a]/30 bg-[#ff2a2a]/5 p-3 font-mono-d8 text-[10px] text-[#ff2a2a]">
                                {exportPkg.production_export_header}
                            </div>
                        )}

                        {/* Audit references */}
                        <div className="space-y-1">
                            <div className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest">Audit References</div>
                            <div className="border border-[#262626] bg-[#0a0a0a] p-3">
                                {exportPkg.audit_references?.map((ref, i) => (
                                    <div key={i} className="font-mono-d8 text-[10px] text-[#8a8a8a]">{ref}</div>
                                ))}
                            </div>
                        </div>

                        {/* Copy only — no upload, no publish */}
                        <div className="flex gap-2">
                            <button
                                data-testid="copy-production-export-btn"
                                onClick={copyJson}
                                className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#ff2a2a] text-[#8a8a8a] hover:text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-widest px-4 py-2 transition-colors"
                            >
                                <Copy className="w-3 h-3" />
                                {copied ? "Copied!" : "Copy JSON"}
                            </button>
                        </div>

                        <pre
                            data-testid="production-export-json"
                            className="bg-[#0a0a0a] border border-[#262626] p-4 font-mono-d8 text-[11px] text-[#f5f5f0] overflow-x-auto max-h-[500px] overflow-y-auto"
                        >
                            {JSON.stringify(exportPkg, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};
